import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AutoComplete } from 'primeng/autocomplete';

import { PessoaDTO } from '../../../models/clientes/pessoa.model';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import {
  EntitySelectorConfig,
  EntitySelectorColumn,
  DEFAULT_ENTITY_COLUMNS,
} from './entity-selector.interfaces';

@Component({
  selector: 'app-entity-selector',
  templateUrl: './entity-selector.component.html',
  styleUrls: ['./entity-selector.component.scss'],
})
export class EntitySelectorComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('buscaInput') buscaInput!: ElementRef;
  @ViewChild('autoCompleteRef') autoCompleteRef?: AutoComplete;

  // Inputs
  @Input() config!: EntitySelectorConfig;
  @Input() value: PessoaDTO | undefined;
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() addNewLabel: string = '';
  // Classe opcional para o painel do autocomplete (ex.: elevar z-index acima de sidebar/modal)
  @Input() panelStyleClass: string = '';

  // Outputs
  @Output() valueChange = new EventEmitter<PessoaDTO | undefined>();
  @Output() entitySelected = new EventEmitter<PessoaDTO>();
  @Output() addNew = new EventEmitter<void>();
  @Output() dialogOpened = new EventEmitter<void>();
  @Output() dialogClosed = new EventEmitter<void>();

  // Autocomplete
  autoCompleteValue: any = null;
  filteredEntities: PessoaDTO[] = [];
  private searchSubject = new Subject<string>();

  // Dialog
  dialogVisible: boolean = false;
  entities: PessoaDTO[] = [];
  selectedPessoa: PessoaDTO | undefined;
  loading: boolean = false;

  // Paginacao
  rows: number = 10;
  totalRecords: number = 0;
  paginaAtual: number = 0;
  opcoesLinhasPorPagina = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  // Ordenacao
  lastField = 'id';
  lastSort = 'asc';

  // Busca no dialog
  searchNome: string = '';

  // Colunas resolvidas
  columns: EntitySelectorColumn[] = [];

  // Handlers de teclado para dialog
  private enterHandler = (event: KeyboardEvent) => this.handleEnterKey(event);
  private escHandler = (event: KeyboardEvent) => this.handleEscKey(event);

  constructor(
    private pessoaService: PessoaService,
    private translate: TranslateService,
    private ngZone: NgZone
  ) {}

  // --- Ciclo de vida ---

  ngOnInit(): void {
    // Resolver colunas (usar default se nao informado)
    this.columns = this.config.dialogColumns?.length
      ? this.config.dialogColumns
      : this.buildDefaultColumns();

    // Sincronizar autoCompleteValue com value inicial
    if (this.value) {
      this.autoCompleteValue = this.value;
    }

    // Label padrao para o botao "+ Agregar" no dropdown
    if (!this.addNewLabel) {
      this.addNewLabel = this.translate.instant('entity-selector.buttons.agregar_persona');
    }

    // Debounce para busca no autocomplete (150ms para resposta rapida)
    this.searchSubject.pipe(debounceTime(150)).subscribe((query: string) => {
      this.searchEntitiesAutocomplete(query);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Sincronizar autoCompleteValue quando value muda externamente
    if (changes['value']) {
      this.autoCompleteValue = this.value || null;
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.removeDialogKeyListeners();
  }

  // --- Propriedades calculadas ---

  get totalPaginas(): number {
    return Math.ceil(this.totalRecords / this.rows) || 1;
  }

  // --- Autocomplete ---

  onAutoComplete(event: any): void {
    this.searchSubject.next(event.query);
  }

  onEntitySelect(event: any): void {
    const entity = event.value ?? event;

    this.value = entity;
    this.autoCompleteValue = entity;
    this.valueChange.emit(this.value);
    this.entitySelected.emit(entity);
  }

  onClear(): void {
    this.clear();
  }

  /** Metodo publico para limpar o valor selecionado */
  clear(): void {
    this.value = undefined;
    this.autoCompleteValue = null;
    this.valueChange.emit(undefined);
  }

  // --- Dialog ---

  /** Metodo publico para abrir o dialog (usado pelo pai para F3) */
  openDialog(): void {
    if (this.disabled) return;

    this.searchNome = '';
    this.paginaAtual = 0;
    this.selectedPessoa = undefined;
    this.lastField = 'id';
    this.lastSort = 'asc';
    this.dialogVisible = true;
    this.dialogOpened.emit();

    this.addDialogKeyListeners();
    this.findEntities();

    // Focar no input de busca apos abrir
    setTimeout(() => {
      if (this.buscaInput?.nativeElement) {
        this.buscaInput.nativeElement.focus();
      }
    }, 200);
  }

  closeDialog(): void {
    this.dialogVisible = false;
    this.selectedPessoa = undefined;
    this.removeDialogKeyListeners();
    this.dialogClosed.emit();
  }

  // --- Selecao no dialog ---

  onRowSelect(_event: any): void {
    // Apenas marca a selecao, nao confirma
  }

  onRowDblClick(entity: PessoaDTO): void {
    this.selectedPessoa = entity;
    this.confirmarSelecao();
  }

  confirmarSelecao(): void {
    if (this.selectedPessoa) {
      this.value = this.selectedPessoa;
      this.autoCompleteValue = this.selectedPessoa;
      this.valueChange.emit(this.value);
      this.entitySelected.emit(this.selectedPessoa);
      this.closeDialog();
    }
  }

  // --- Paginacao ---

  irParaPagina(pagina: number): void {
    if (pagina < 0 || pagina >= this.totalPaginas) return;
    this.paginaAtual = pagina;
    this.findEntities(this.paginaAtual, this.rows, `${this.lastField},${this.lastSort}`);
  }

  onLinhasPorPaginaChange(): void {
    this.paginaAtual = 0;
    this.findEntities(0, this.rows, `${this.lastField},${this.lastSort}`);
  }

  // --- Ordenacao ---

  customSort(event: any): void {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    }

    if (this.lastField === event.field && this.lastSort === sort) {
      return;
    }

    this.lastField = String(event.field);
    this.lastSort = String(sort);

    this.paginaAtual = 0;
    this.findEntities(0, this.rows, `${this.lastField},${this.lastSort}`);
  }

  // --- Busca no dialog ---

  onSearchKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' || this.searchNome.trim() === '' || this.searchNome.length >= 3) {
      this.paginaAtual = 0;
      this.findEntities(0, this.rows, `${this.lastField},${this.lastSort}`);
    }
  }

  // --- Utilitarios ---

  /**
   * Resolve campo aninhado (ex: 'cidade.nome' -> entity.cidade?.nome)
   */
  resolveField(entity: any, field: string): any {
    return field.split('.').reduce((obj, key) => obj?.[key], entity) ?? '';
  }

  // --- Metodos privados ---

  /**
   * Constroi colunas padrao com headers traduzidos em runtime.
   *
   * Estrategia: DEFAULT_ENTITY_COLUMNS tem headers vazios.
   * Aqui preenchemos via translate.instant() para que as colunas
   * funcionem automaticamente sem o pai precisar informar headers.
   */
  private buildDefaultColumns(): EntitySelectorColumn[] {
    const headerKeys: Record<string, string> = {
      codigoPessoa: 'entity-selector.headers.codigo',
      nome: 'entity-selector.headers.nome',
      fantasia: 'entity-selector.headers.nome_fantasia',
      telefone: 'entity-selector.headers.telefone',
      ci: 'entity-selector.headers.ci',
      ruc: 'entity-selector.headers.ruc',
      'cidade.nome': 'entity-selector.headers.cidade',
    };

    return DEFAULT_ENTITY_COLUMNS.map((col) => ({
      ...col,
      header: this.translate.instant(headerKeys[col.field] || col.field),
    }));
  }

  /**
   * Busca entidades no autocomplete (com debounce via Subject)
   */
  private searchEntitiesAutocomplete(query: string): void {
    const filter = `${this.config.categoryFilter}${this.buildSearchFilter(query)}`;

    this.pessoaService
      .get('/pessoas/buscar', {
        page: 0,
        size: 30,
        sort: 'id,asc',
        filter,
      })
      .subscribe(
        (response: any) => {
          this.filteredEntities = response.content || [];
        },
        (_error: any) => {
          this.filteredEntities = [];
        }
      );
  }

  /**
   * Handler do clique no item "+ Agregar Persona" dentro do dropdown.
   * Fecha o dropdown e emite o evento addNew para o componente pai.
   */
  onAddNewFromDropdown(event: Event): void {
    event.stopPropagation();
    // Limpar autocomplete e forcar fechamento do overlay do PrimeNG
    this.filteredEntities = [];
    this.autoCompleteValue = null;
    this.autoCompleteRef?.hide();
    const inputEl = this.autoCompleteRef?.el?.nativeElement?.querySelector(
      'input'
    ) as HTMLInputElement | undefined;
    inputEl?.blur();
    this.addNew.emit();
  }

  /** Abre o dialog de adicionar pessoa a partir do dialog de selecao */
  onAddNewFromDialog(): void {
    this.addNew.emit();
  }

  /**
   * Busca entidades para o dialog (paginacao server-side)
   */
  private findEntities(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ): void {
    this.loading = true;
    const filter = `${this.config.categoryFilter}${this.buildSearchFilter(this.searchNome)}`;

    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter,
      })
      .subscribe(
        (response: any) => {
          this.entities = response.content;
          this.totalRecords = response.totalElements;
          this.paginaAtual = page;
          this.loading = false;
        },
        (_error: any) => {
          this.entities = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      );
  }

  /**
   * Constroi a string de filtro para a API baseado no termo de busca.
   * Replica o padrao existente no FornecedorSelecionarComponent.
   */
  private buildSearchFilter(searchTerm: string): string {
    if (!searchTerm) return '';

    let filter = '';
    filter += `&email=${searchTerm}`;
    filter += `&nome=${searchTerm}`;
    filter += `&fantasia=${searchTerm}`;
    filter += `&ruc=${searchTerm}`;
    filter += `&telefone=${searchTerm}`;
    filter += `&ci=${searchTerm}`;
    filter += `&nomeCidade=${searchTerm}`;

    if (!isNaN(Number(searchTerm))) {
      filter += `&codigoPessoa=${searchTerm}`;
    }

    return filter;
  }

  // --- Keyboard handlers do dialog ---

  private addDialogKeyListeners(): void {
    document.addEventListener('keydown', this.enterHandler, true);
    document.addEventListener('keydown', this.escHandler, true);
  }

  private removeDialogKeyListeners(): void {
    document.removeEventListener('keydown', this.enterHandler, true);
    document.removeEventListener('keydown', this.escHandler, true);
  }

  private handleEnterKey(event: KeyboardEvent): void {
    if (!this.dialogVisible || event.key !== 'Enter') return;
    if (!this.selectedPessoa) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.ngZone.run(() => this.confirmarSelecao());
  }

  private handleEscKey(event: KeyboardEvent): void {
    if (!this.dialogVisible || event.key !== 'Escape') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.ngZone.run(() => this.closeDialog());
  }
}
